import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const TechnicianManager = ({ tecnicos, onToast }) => {
    const { user } = useAuth();
    const [technicianList, setTechnicianList] = useState(tecnicos);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedTechnician, setSelectedTechnician] = useState(null);
    const [assignmentData, setAssignmentData] = useState({
        distrito: '',
        cluster: ''
    });

    // Datos de ejemplo para distritos y clusters por región
    const regionData = {
        'Norte': {
            distritos: ['Distrito Norte 1', 'Distrito Norte 2', 'Distrito Norte 3'],
            clusters: {
                'Distrito Norte 1': ['Cluster A1', 'Cluster A2', 'Cluster A3'],
                'Distrito Norte 2': ['Cluster B1', 'Cluster B2', 'Cluster B3'],
                'Distrito Norte 3': ['Cluster C1', 'Cluster C2', 'Cluster C3']
            }
        },
        'Sur': {
            distritos: ['Distrito Sur 1', 'Distrito Sur 2', 'Distrito Sur 3'],
            clusters: {
                'Distrito Sur 1': ['Cluster D1', 'Cluster D2', 'Cluster D3'],
                'Distrito Sur 2': ['Cluster E1', 'Cluster E2', 'Cluster E3'],
                'Distrito Sur 3': ['Cluster F1', 'Cluster F2', 'Cluster F3']
            }
        },
        'Centro': {
            distritos: ['Distrito Centro 1', 'Distrito Centro 2'],
            clusters: {
                'Distrito Centro 1': ['Cluster G1', 'Cluster G2'],
                'Distrito Centro 2': ['Cluster H1', 'Cluster H2']
            }
        }
    };

    useEffect(() => {
        setTechnicianList(tecnicos);
    }, [tecnicos]);

    const openAssignModal = (technician) => {
        setSelectedTechnician(technician);
        setAssignmentData({
            distrito: technician.distrito || '',
            cluster: technician.cluster || ''
        });
        setShowAssignModal(true);
    };

    const closeAssignModal = () => {
        setShowAssignModal(false);
        setSelectedTechnician(null);
        setAssignmentData({ distrito: '', cluster: '' });
    };

    const handleAssignment = async () => {
        if (!assignmentData.distrito || !assignmentData.cluster) {
            onToast('Por favor selecciona distrito y cluster', 'error');
            return;
        }

        try {
            // Aquí iría la llamada a la API para actualizar en la BD
            const response = await fetch('/api/coordinator/assign-territory', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    technicianId: selectedTechnician.id,
                    distrito: assignmentData.distrito,
                    cluster: assignmentData.cluster,
                    coordinatorId: user.id
                })
            });

            if (response.ok) {
                // Actualizar la lista local
                const updatedTechnicians = technicianList.map(tech =>
                    tech.id === selectedTechnician.id
                        ? { ...tech, ...assignmentData, status: 'active' }
                        : tech
                );
                setTechnicianList(updatedTechnicians);

                onToast(`Distrito y cluster asignados a ${selectedTechnician.name}`, 'success');
                closeAssignModal();
            } else {
                onToast('Error al asignar territorio', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            onToast('Error de conexión', 'error');
        }
    };

    const toggleTechnicianStatus = async (technicianId) => {
        try {
            const technician = technicianList.find(t => t.id === technicianId);
            const newStatus = technician.status === 'active' ? 'inactive' : 'active';

            const response = await fetch(`/api/coordinator/technician/${technicianId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (response.ok) {
                const updatedTechnicians = technicianList.map(tech =>
                    tech.id === technicianId
                        ? { ...tech, status: newStatus }
                        : tech
                );
                setTechnicianList(updatedTechnicians);

                onToast(`Estado actualizado a ${newStatus === 'active' ? 'activo' : 'inactivo'}`, 'success');
            } else {
                onToast('Error al actualizar estado', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            onToast('Error de conexión', 'error');
        }
    };

    const availableDistritos = regionData[user?.region]?.distritos || [];
    const availableClusters = assignmentData.distrito
        ? regionData[user?.region]?.clusters[assignmentData.distrito] || []
        : [];

    return (
        <div className="technician-manager">
            <div className="section-header">
                <h2>👥 Gestión de Técnicos</h2>
                <p>Administra y asigna territorios a tus técnicos</p>
            </div>

            <div className="technicians-grid">
                {technicianList.map((technician) => (
                    <div key={technician.id} className="technician-card">
                        <div className="technician-header">
                            <div className="technician-info">
                                <h3>{technician.name}</h3>
                                <p className="technician-email">{technician.email}</p>
                            </div>
                            <div className="technician-status">
                                <span className={`status-badge ${technician.status}`}>
                                    {technician.status === 'active' ? '🟢 Activo' : '🔴 Inactivo'}
                                </span>
                            </div>
                        </div>

                        <div className="technician-assignments">
                            <div className="assignment-item">
                                <span className="assignment-label">📍 Distrito:</span>
                                <span className="assignment-value">
                                    {technician.distrito || 'No asignado'}
                                </span>
                            </div>
                            <div className="assignment-item">
                                <span className="assignment-label">🎯 Cluster:</span>
                                <span className="assignment-value">
                                    {technician.cluster || 'No asignado'}
                                </span>
                            </div>
                        </div>

                        <div className="technician-actions">
                            <button
                                className="btn btn-primary"
                                onClick={() => openAssignModal(technician)}
                            >
                                📋 Asignar Territorio
                            </button>
                            <button
                                className={`btn ${technician.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                                onClick={() => toggleTechnicianStatus(technician.id)}
                            >
                                {technician.status === 'active' ? '⏸️ Desactivar' : '▶️ Activar'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {technicianList.length === 0 && (
                <div className="empty-state">
                    <div className="empty-icon">👥</div>
                    <h3>No hay técnicos registrados</h3>
                    <p>Los técnicos aparecerán aquí una vez que se registren en el sistema</p>
                </div>
            )}

            {/* Modal de Asignación */}
            {showAssignModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>📋 Asignar Territorio</h3>
                            <button className="modal-close" onClick={closeAssignModal}>×</button>
                        </div>

                        <div className="modal-body">
                            <div className="technician-summary">
                                <h4>👨‍🔧 {selectedTechnician?.name}</h4>
                                <p>{selectedTechnician?.email}</p>
                            </div>

                            <div className="assignment-form">
                                <div className="form-group">
                                    <label>📍 Distrito</label>
                                    <select
                                        value={assignmentData.distrito}
                                        onChange={(e) => setAssignmentData({
                                            ...assignmentData,
                                            distrito: e.target.value,
                                            cluster: '' // Reset cluster when distrito changes
                                        })}
                                        className="form-select"
                                    >
                                        <option value="">Seleccionar distrito...</option>
                                        {availableDistritos.map(distrito => (
                                            <option key={distrito} value={distrito}>
                                                {distrito}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>🎯 Cluster</label>
                                    <select
                                        value={assignmentData.cluster}
                                        onChange={(e) => setAssignmentData({
                                            ...assignmentData,
                                            cluster: e.target.value
                                        })}
                                        className="form-select"
                                        disabled={!assignmentData.distrito}
                                    >
                                        <option value="">Seleccionar cluster...</option>
                                        {availableClusters.map(cluster => (
                                            <option key={cluster} value={cluster}>
                                                {cluster}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {assignmentData.distrito && (
                                    <div className="assignment-preview">
                                        <h5>📋 Resumen de Asignación:</h5>
                                        <div className="preview-item">
                                            <strong>Técnico:</strong> {selectedTechnician?.name}
                                        </div>
                                        <div className="preview-item">
                                            <strong>Región:</strong> {user?.region}
                                        </div>
                                        <div className="preview-item">
                                            <strong>Distrito:</strong> {assignmentData.distrito}
                                        </div>
                                        <div className="preview-item">
                                            <strong>Cluster:</strong> {assignmentData.cluster || 'Pendiente de selección'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={closeAssignModal}>
                                Cancelar
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleAssignment}
                                disabled={!assignmentData.distrito || !assignmentData.cluster}
                            >
                                ✅ Confirmar Asignación
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .technician-manager {
                    width: 100%;
                }

                .section-header {
                    margin-bottom: 30px;
                }

                .section-header h2 {
                    margin: 0 0 5px 0;
                    color: #333;
                    font-size: 1.5rem;
                }

                .section-header p {
                    margin: 0;
                    color: #666;
                }

                .technicians-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
                    gap: 20px;
                }

                .technician-card {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.07);
                    border-left: 4px solid #28a745;
                    transition: transform 0.2s ease;
                }

                .technician-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 12px rgba(0,0,0,0.1);
                }

                .technician-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 15px;
                }

                .technician-info h3 {
                    margin: 0 0 5px 0;
                    color: #333;
                    font-size: 1.1rem;
                }

                .technician-email {
                    margin: 0;
                    color: #666;
                    font-size: 0.9rem;
                }

                .status-badge {
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 0.8rem;
                    font-weight: 600;
                }

                .status-badge.active {
                    background: #d4edda;
                    color: #155724;
                }

                .status-badge.inactive {
                    background: #f8d7da;
                    color: #721c24;
                }

                .technician-assignments {
                    margin-bottom: 20px;
                    padding: 15px;
                    background: #f8f9fa;
                    border-radius: 8px;
                }

                .assignment-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                }

                .assignment-item:last-child {
                    margin-bottom: 0;
                }

                .assignment-label {
                    font-weight: 600;
                    color: #555;
                }

                .assignment-value {
                    color: #333;
                    font-weight: 500;
                }

                .technician-actions {
                    display: flex;
                    gap: 10px;
                }

                .btn {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    flex: 1;
                    font-size: 0.9rem;
                }

                .btn-primary {
                    background: #667eea;
                    color: white;
                }

                .btn-primary:hover {
                    background: #5a67d8;
                }

                .btn-warning {
                    background: #ffc107;
                    color: #212529;
                }

                .btn-warning:hover {
                    background: #e0a800;
                }

                .btn-success {
                    background: #28a745;
                    color: white;
                }

                .btn-success:hover {
                    background: #218838;
                }

                .btn-secondary {
                    background: #6c757d;
                    color: white;
                }

                .btn-secondary:hover {
                    background: #5a6268;
                }

                .empty-state {
                    text-align: center;
                    padding: 60px 20px;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.07);
                }

                .empty-icon {
                    font-size: 4rem;
                    margin-bottom: 20px;
                }

                .empty-state h3 {
                    margin: 0 0 10px 0;
                    color: #333;
                }

                .empty-state p {
                    margin: 0;
                    color: #666;
                }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }

                .modal-content {
                    background: white;
                    border-radius: 12px;
                    width: 90%;
                    max-width: 500px;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px;
                    border-bottom: 1px solid #e9ecef;
                }

                .modal-header h3 {
                    margin: 0;
                    color: #333;
                }

                .modal-close {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: #666;
                    padding: 0;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .modal-close:hover {
                    color: #333;
                }

                .modal-body {
                    padding: 20px;
                }

                .technician-summary {
                    background: #f8f9fa;
                    padding: 15px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    text-align: center;
                }

                .technician-summary h4 {
                    margin: 0 0 5px 0;
                    color: #333;
                }

                .technician-summary p {
                    margin: 0;
                    color: #666;
                    font-size: 0.9rem;
                }

                .assignment-form {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .form-group label {
                    font-weight: 600;
                    color: #333;
                }

                .form-select {
                    padding: 10px;
                    border: 2px solid #e9ecef;
                    border-radius: 6px;
                    font-size: 1rem;
                    transition: border-color 0.3s ease;
                }

                .form-select:focus {
                    outline: none;
                    border-color: #667eea;
                }

                .form-select:disabled {
                    background: #f8f9fa;
                    color: #6c757d;
                    cursor: not-allowed;
                }

                .assignment-preview {
                    background: #e7f3ff;
                    padding: 15px;
                    border-radius: 8px;
                    border-left: 4px solid #007bff;
                }

                .assignment-preview h5 {
                    margin: 0 0 10px 0;
                    color: #333;
                }

                .preview-item {
                    margin-bottom: 5px;
                    color: #333;
                }

                .modal-footer {
                    padding: 20px;
                    border-top: 1px solid #e9ecef;
                    display: flex;
                    gap: 10px;
                    justify-content: flex-end;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn:disabled:hover {
                    transform: none;
                }

                @media (max-width: 768px) {
                    .technicians-grid {
                        grid-template-columns: 1fr;
                    }

                    .technician-actions {
                        flex-direction: column;
                    }

                    .modal-content {
                        width: 95%;
                        margin: 10px;
                    }

                    .modal-footer {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
};

export default TechnicianManager;